/**
 * ScopeRibbon (W1 S2b — Screen Contract G01, "Scope ribbon").
 *
 * The launch pack's Screen Contract sheet defines G01's purpose as: "Make
 * the exact read context visible and reproducible while preventing a hidden
 * DEV, first-portal, last-tab, or stale browser default from becoming an
 * action target." Concretely: this bar shows exactly what ScopeContext
 * derives from the current URL, and nothing it doesn't — no environment in
 * the URL renders an explicit "All environments" read-only indicator (the
 * docx rule referenced in the story: missing environment = all/read-only,
 * never a silent default).
 *
 * THIS RIBBON IS AN INDICATOR, NOT A CONTROL. It used to render its own
 * <select> over the same four environments the header's EnvironmentSelector
 * already offers, which put two controls for one field on every scoped page.
 * The header control is now the single place the environment is changed (a
 * real router navigation via ScopeContext, so Back/reload/copied links restore
 * the scope shown here); this bar only reports the result. The dirty-form
 * guard moved with the control — see utils/dirtyGuard.ts and Header.tsx.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are S0's (components/layout/*), outside this task's file ownership. See
 * the PR body for the mount-site handoff to the Stream 7 (G01-G11 shell
 * closure) task.
 */
import React from 'react';
import { useScope } from '../../contexts/ScopeContext';
import { ENVIRONMENTS, PROVISIONED_ENVIRONMENTS, type Environment } from '../../contexts/EnvironmentContext';
import { useShellOwns } from '../../chrome/ShellChromeContext';

function isEnvironment(value: string | null): value is Environment {
  return value !== null && (ENVIRONMENTS as string[]).includes(value);
}

export interface ScopeRibbonProps {
  /**
   * True only for the shell's own instance in Layout.tsx.
   *
   * Story #5343. Layout mounts the ribbon for every route, and 24 pages also
   * mount their own — so from the moment W1_SHELL went true in production
   * (2026-09-02) those 24 screens rendered two scope bars. A page-level
   * instance now renders null instead. Fixing it here rather than deleting 24
   * page-level mounts keeps the pages working when they are rendered outside
   * the shell, in tests and on unchromed routes.
   */
  shell?: boolean;
}

export const ScopeRibbon: React.FC<ScopeRibbonProps> = ({ shell = false }) => {
  const supersededByShell = useShellOwns('scopeRibbon', shell);
  const scope = useScope();

  const environmentId = scope.environmentId;
  const hasEnvironment = isEnvironment(environmentId);

  // After every hook, never before — the shell already drew this ribbon.
  if (supersededByShell) return null;

  return (
    <div
      role="region"
      aria-label="Scope"
      data-testid="scope-ribbon"
      className="flex flex-wrap items-center gap-3 px-4 lg:px-6 py-2 border-b border-gray-200 bg-white text-sm"
    >
      <span className="font-medium text-gray-500">Scope</span>

      {hasEnvironment ? (
        // DISPLAY, not a control. This used to be a full <select> over the same
        // four environments the header's EnvironmentSelector already offers, so
        // whenever an environment was in scope the page carried two controls
        // for one field — Rob's "duplicated" — and this file's own docstring
        // above already says the ribbon exists to "make the exact read context
        // VISIBLE". The header segmented control is the one place you change
        // it; this is where you read what it currently is.
        <span className="flex items-center gap-1.5">
          <span className="text-gray-500">Environment</span>
          <span
            data-testid="scope-ribbon-environment"
            className="rounded bg-iflow-50 px-2 py-0.5 font-semibold uppercase text-iflow-900"
          >
            {environmentId}
          </span>
          {!PROVISIONED_ENVIRONMENTS.includes(environmentId as Environment) && (
            <span className="text-xs text-amber-700">not provisioned</span>
          )}
        </span>
      ) : (
        <span
          data-testid="scope-ribbon-all-environments"
          title="No environment is selected in the URL — showing the estate-wide, read-only view (never a hidden default)"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
          All environments · read-only
        </span>
      )}

      {scope.portalId && (
        <span className="flex items-center gap-1.5 text-gray-500">
          <span>Portal</span>
          <span className="font-medium text-iflow-900">{scope.portalId}</span>
        </span>
      )}
    </div>
  );
};

export default ScopeRibbon;
